import { Link } from "react-router-dom";
import { CreditCard, Mail, Phone, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { SocialLinks } from "./SocialLinks";
import { CONTACT, PAYMENT_URL, WHATSAPP_URL, NAV_LINKS, SUPPORT_TYPES } from "./data";
import { scrollToSection } from "@/lib/scrollTo";

const LEGAL_LINKS = [
  { label: "Why Choose Us", to: "/why-choose-us" },
  { label: "Refund Policy", to: "/refund-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Revision Policy", to: "/revision-policy" },
  { label: "Cancellation Policy", to: "/cancellation-policy" },
];

export const Footer = () => (
  <footer className="border-t border-[#A07E1F]/20 bg-[#F3EEE2]" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-1">
          <Logo />
          <p className="mt-6 max-w-xs text-sm leading-relaxed text-[#46586F]">
            Premium academic support for students who take their grades seriously. Human experts,
            original research, delivered on time — worldwide.
          </p>
          <div className="mt-6">
            <p className="mb-3 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#A07E1F]">Connect With Us</p>
            <SocialLinks testidPrefix="footer-social" dark />
          </div>
        </div>
        <div>
          <p className="mb-5 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#A07E1F]">Explore</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <button onClick={() => scrollToSection(l.href)} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-[#46586F] transition-colors duration-300 hover:text-[#A07E1F]">
                  {l.label}
                </button>
              </li>
            ))}
            <li>
              <Link to="/order" data-testid="footer-link-order" className="text-sm font-semibold text-[#A07E1F] transition-colors duration-300 hover:text-[#12294D]">
                Order Now
              </Link>
            </li>
            <li>
              <Link to="/blog" data-testid="footer-link-blog" className="text-sm text-[#46586F] transition-colors duration-300 hover:text-[#A07E1F]">
                Blog & Study Guides
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="mb-5 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#A07E1F]">Services</p>
          <ul className="space-y-3">
            {SUPPORT_TYPES.map((s) => (
              <li key={s}>
                <button onClick={() => scrollToSection("#services")} data-testid={`footer-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm text-[#46586F] transition-colors duration-300 hover:text-[#A07E1F]">
                  {s}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mb-5 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#A07E1F]">Legal</p>
          <ul className="space-y-3">
            {LEGAL_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-legal-${l.to.slice(1)}`} className="text-sm text-[#46586F] transition-colors duration-300 hover:text-[#A07E1F]">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="mb-5 font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#A07E1F]">Contact</p>
          <ul className="space-y-4 text-sm text-[#46586F]">
            <li>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="flex items-center gap-3 break-all transition-colors duration-300 hover:text-[#A07E1F]">
                <Mail size={15} className="shrink-0 text-[#A07E1F]" /> {CONTACT.email}
              </a>
            </li>
            <li>
              <a href="tel:+442073626786" data-testid="footer-phone-link" className="flex items-center gap-3 transition-colors duration-300 hover:text-[#A07E1F]">
                <Phone size={15} className="shrink-0 text-[#A07E1F]" /> {CONTACT.phone}
              </a>
            </li>
            <li>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" className="flex items-center gap-3 transition-colors duration-300 hover:text-[#A07E1F]">
                <MessageCircle size={15} className="shrink-0 text-[#A07E1F]" /> {CONTACT.whatsapp}
              </a>
            </li>
          </ul>
          <a
            href={PAYMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-pay-now-button"
            className="mt-7 inline-flex items-center gap-2 rounded-full border border-[#A07E1F]/50 px-5 py-2.5 font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#A07E1F] transition-colors duration-300 hover:bg-[#D4AF37]/15"
          >
            <CreditCard size={13} /> Pay Securely Online
          </a>
        </div>
      </div>
      <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-[#A07E1F]/15 pt-8 sm:flex-row sm:items-center">
        <p className="text-xs text-[#5E6E85]" data-testid="footer-copyright">
          © 2026 MyHomeworkAssist · myhomeworkassist.online · All rights reserved.
        </p>
        <p className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#75849B]">
          AI-Free · Plagiarism-Free · 100% Confidential
        </p>
      </div>
    </div>
  </footer>
);
